<template>
  <section class="delivery-tax-panel">
    <header class="delivery-tax-panel__header">
      <div>
        <h5>Taxe de livrare aplicate</h5>
        <p>{{ deliveryRangeId ? 'Taxele de mai jos se aplică acestei zone.' : 'Taxele de mai jos se aplică acestei locații.' }}</p>
      </div>
      <div class="delivery-tax-panel__actions">
        <button v-if="deliveryRangeId" type="button" class="delivery-tax-action is-secondary" @click.prevent="openAssignment"><i class="ti-link"></i> Atribuie taxe existente</button>
        <button type="button" class="delivery-tax-action" @click.prevent="openCreate"><i class="ti-plus"></i> Creează taxă</button>
      </div>
    </header>

    <div v-if="loading" class="delivery-tax-empty">Se încarcă taxele...</div>
    <div v-else-if="!appliedTaxes.length" class="delivery-tax-empty">
      <i class="fa fa-info-circle"></i>
      Nu există taxe de livrare aplicate. Până la configurarea primei taxe, costul vechi rămâne activ pentru compatibilitate.
    </div>
    <div v-else class="delivery-tax-list">
      <article v-for="tax in appliedTaxes" :key="tax.id" class="delivery-tax-row">
        <div class="delivery-tax-row__title">
          <strong>{{ tax.name }}</strong>
          <span :class="tax.is_active ? 'is-active' : 'is-inactive'">{{ tax.is_active ? 'Activă' : 'Inactivă' }}</span>
        </div>
        <div class="delivery-tax-row__details">
          <span><small>Cost standard</small><b>{{ money(tax.value) }} {{ currency }}</b></span>
          <span><small>TVA</small><b>{{ tax.vat_rate ? rate(tax.vat_rate.rate) + '%' : '-' }}</b></span>
          <span><small>POS ID</small><b>{{ tax.pos_id || '-' }}</b></span>
          <span><small>Intervale</small><b>{{ (tax.delivery_cost_ranges || []).length }}</b></span>
        </div>
        <button type="button" class="delivery-tax-edit" @click.prevent="openEdit(tax)"><i class="fa fa-pencil"></i> Editează</button>
      </article>
    </div>

    <div v-if="assignmentOpen" ref="assignmentEditor" class="delivery-tax-editor" @click.self="closeAssignment">
      <div class="delivery-tax-editor__page delivery-tax-assignment-page">
        <header class="delivery-tax-editor__topbar">
          <div class="delivery-tax-editor__heading">
            <button type="button" class="delivery-tax-back" @click.prevent="closeAssignment"><i class="ti-close"></i> Renunță</button>
            <div><h3>Atribuie taxe existente</h3><p>Selectează una sau mai multe taxe de livrare pentru zona curentă.</p></div>
          </div>
          <button type="button" class="delivery-tax-save" :disabled="assigning || !selectedExistingTaxIds.length" @click.prevent="assignExistingTaxes"><img src="/static/img/icons/save.png">{{ assigning ? 'Se salvează...' : 'Atribuie' }}</button>
        </header>

        <main class="delivery-tax-editor__body">
          <section class="delivery-tax-editor__section">
            <div class="delivery-tax-editor__section-title"><h4>Taxe disponibile</h4><p>Detaliile taxelor rămân neschimbate. Se adaugă numai această zonă în aplicabilitatea lor.</p></div>
            <div v-if="!assignableTaxes.length" class="delivery-tax-empty">Toate taxele de livrare existente se aplică deja acestei zone.</div>
            <div v-else class="form-group">
              <label>Taxe de livrare</label>
              <el-select v-model="selectedExistingTaxIds" multiple filterable collapse-tags clearable class="delivery-tax-select" placeholder="Selectează taxele">
                <el-option v-for="tax in assignableTaxes" :key="tax.id" :label="existingTaxLabel(tax)" :value="tax.id"></el-option>
              </el-select>
              <small class="delivery-tax-field-hint">Poți selecta mai multe taxe. Taxele inactive rămân inactive după atribuire.</small>
            </div>
          </section>
        </main>
      </div>
    </div>

    <div v-if="editorOpen" ref="editor" class="delivery-tax-editor" @click.self="closeEditor">
      <div class="delivery-tax-editor__page">
        <header class="delivery-tax-editor__topbar">
          <div class="delivery-tax-editor__heading">
            <button type="button" class="delivery-tax-back" @click.prevent="closeEditor"><i class="ti-close"></i> Renunță</button>
            <div><h3>{{ form.id ? form.name : 'Adaugă taxă de livrare' }}</h3><p>{{ deliveryRangeId ? 'Aplicabilitate: zona curentă' : 'Aplicabilitate: locația curentă' }}</p></div>
          </div>
          <button type="button" class="delivery-tax-save" :disabled="saving || editorLoading" @click.prevent="save"><img src="/static/img/icons/save.png">{{ saving ? 'Se salvează...' : 'Salvează' }}</button>
        </header>

        <div v-if="editorLoading" class="delivery-tax-editor__loading">Se încarcă taxa...</div>
        <main v-else class="delivery-tax-editor__body">
          <section class="delivery-tax-editor__section">
            <div class="delivery-tax-editor__section-title"><h4>Detalii taxă</h4><p>Taxa va apărea ca linie separată în coș și pe comandă.</p></div>
            <div class="delivery-tax-form-grid">
              <div class="form-group is-wide"><label>Nume taxă</label><input v-model.trim="form.name" type="text" class="form-control" placeholder="Ex: Livrare"></div>
              <div class="form-group"><label>Cost livrare (lei)</label><input v-model.number="form.value" type="number" min="0" step="0.01" class="form-control"></div>
              <div class="form-group"><label>TVA obligatoriu</label><select v-model="form.vat_rate_id" class="form-control"><option disabled value="">Selectează TVA</option><option v-for="vat in selectableVatRates" :key="vat.id" :value="vat.id" :disabled="!vat.is_active">{{ rate(vat.rate) }}%{{ vat.is_active ? '' : ' (inactiv)' }}</option></select></div>
              <div class="form-group"><label>POS ID pentru cost livrare</label><input v-model.trim="form.pos_id" type="text" class="form-control" placeholder="Opțional"></div>
              <div class="form-group"><label>Surse</label><el-select v-model="form.source_ids" multiple filterable collapse-tags clearable class="delivery-tax-select" placeholder="Toate sursele"><el-option v-for="source in sources" :key="source.id" :label="source.name" :value="source.id"></el-option></el-select></div>
              <div class="form-group"><label>Stare</label><label class="delivery-tax-switch"><input v-model="form.is_active" type="checkbox"><span></span><b>{{ form.is_active ? 'Activă' : 'Inactivă' }}</b></label></div>
            </div>
          </section>

          <section class="delivery-tax-editor__section">
            <div class="delivery-tax-editor__section-title with-action">
              <div><h4>Extra Cost Range</h4><p>Costuri diferențiate în funcție de distanță și valoarea minimă a comenzii.</p></div>
              <button type="button" class="delivery-tax-action" @click.prevent="addCostRange"><i class="ti-plus"></i> Adaugă interval</button>
            </div>
            <div class="delivery-tax-warning"><i class="fa fa-exclamation-triangle"></i><span><strong>Intervalele se aplică doar dacă locația are cheia Google Maps configurată.</strong> Altfel se folosește costul standard.</span></div>
            <div v-if="!form.delivery_cost_ranges.length" class="delivery-tax-empty">Nu există intervale suplimentare.</div>
            <article v-for="(rangeItem, index) in form.delivery_cost_ranges" :key="rangeItem.id || 'range-' + index" class="delivery-tax-range">
              <div class="delivery-tax-range__header"><strong>Interval {{ index + 1 }}</strong><button type="button" @click.prevent="removeCostRange(index)"><i class="ti-trash"></i> Șterge</button></div>
              <div class="delivery-tax-form-grid range-grid">
                <div class="form-group"><label>Distanță până la (km)</label><input v-model.number="rangeItem.distance_in_km" type="number" min="0" step="0.01" class="form-control"></div>
                <div class="form-group"><label>Cost (lei)</label><input v-model.number="rangeItem.shipping_cost" type="number" min="0" step="0.01" class="form-control"></div>
                <div class="form-group"><label>POS ID</label><input v-model.trim="rangeItem.pos_id" type="text" class="form-control" placeholder="Moștenește valoarea standard"></div>
                <div class="form-group"><label>Comandă minimă (lei)</label><input v-model.number="rangeItem.min_order_value" type="number" min="0" step="0.01" class="form-control"></div>
                <div class="form-group"><label>Timp extra (minute)</label><input v-model.number="rangeItem.extra_delivery_time" type="number" min="0" step="1" class="form-control"></div>
              </div>
            </article>
          </section>
        </main>
      </div>
    </div>
  </section>
</template>

<script>
import Vue from 'vue'
import {Select, Option} from 'element-ui'
import swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.css'

Vue.use(Select)
Vue.use(Option)

const emptyForm = () => ({
  id: null,
  name: '',
  applies_to: 'delivery',
  calculation_type: 'fixed',
  value: 0,
  pos_id: '',
  vat_rate_id: '',
  is_active: true,
  location_ids: [],
  source_ids: [],
  method_ids: [3],
  delivery_range_ids: [],
  product_ids: [],
  company_option_value_ids: [],
  delivery_cost_ranges: []
})

export default {
  name: 'DeliveryTaxPanel',
  props: {
    locationId: {default: null},
    deliveryRangeId: {default: null},
    currency: {type: String, default: 'lei'}
  },
  data () {
    return {
      loading: false,
      saving: false,
      editorLoading: false,
      editorOpen: false,
      assignmentOpen: false,
      assigning: false,
      selectedExistingTaxIds: [],
      rules: [],
      vatRates: [],
      form: emptyForm(),
      sources: [
        {id: 1, name: 'Website'},
        {id: 2, name: 'QR'},
        {id: 3, name: 'Kiosk'},
        {id: 4, name: 'POS'},
        {id: 5, name: 'App'},
        {id: 6, name: 'Glovo'},
        {id: 7, name: 'Bolt'},
        {id: 8, name: 'Wolt'}
      ]
    }
  },
  computed: {
    appliedTaxes () {
      return this.rules.filter(rule => {
        if (!rule || rule.applies_to !== 'delivery') return false
        const locations = rule.location_ids || []
        const ranges = rule.delivery_range_ids || []
        const matchesLocation = !this.locationId || !locations.length || locations.some(id => String(id) === String(this.locationId))
        const matchesRange = this.deliveryRangeId ? (!ranges.length || ranges.some(id => String(id) === String(this.deliveryRangeId))) : !ranges.length
        return matchesLocation && matchesRange
      })
    },
    selectableVatRates () {
      return this.vatRates.filter(vat => vat.is_active || String(vat.id) === String(this.form.vat_rate_id))
    },
    assignableTaxes () {
      if (!this.deliveryRangeId) return []
      return this.rules.filter(rule => {
        if (!rule || rule.applies_to !== 'delivery') return false
        const ranges = rule.delivery_range_ids || []
        if (!ranges.length) return false
        return !ranges.some(id => String(id) === String(this.deliveryRangeId))
      })
    }
  },
  watch: {
    locationId () { this.loadRules() },
    deliveryRangeId () { this.loadRules() }
  },
  created () {
    this.loadRules()
    this.loadVatRates()
  },
  beforeDestroy () {
    document.body.classList.remove('delivery-tax-editor-open')
  },
  methods: {
    requestData (extra) {
      return Object.assign({token: this.$session.get('token'), company_id: this.$session.get('selectedCompany')}, extra || {})
    },
    loadRules () {
      this.loading = true
      this.apiRequest('/taxes-v2/rules', 'GET', this.requestData(), response => {
        this.loading = false
        this.rules = response.status ? (response.data || []) : []
      })
    },
    loadVatRates () {
      this.apiRequest('/taxes-v2/vat-rates', 'GET', this.requestData(), response => {
        this.vatRates = response.status ? (response.data || []) : []
      })
    },
    scopeIds (value) {
      if (value === null || value === undefined || value === '') return []
      const numberValue = Number(value)
      return [Number.isNaN(numberValue) ? value : numberValue]
    },
    openCreate () {
      this.form = Object.assign(emptyForm(), {
        location_ids: this.scopeIds(this.locationId),
        delivery_range_ids: this.scopeIds(this.deliveryRangeId)
      })
      this.editorLoading = false
      this.openEditor()
    },
    openAssignment () {
      this.selectedExistingTaxIds = []
      this.assignmentOpen = true
      document.body.classList.add('delivery-tax-editor-open')
      this.$nextTick(() => {
        if (this.$refs.assignmentEditor && this.$refs.assignmentEditor.parentNode !== document.body) {
          document.body.appendChild(this.$refs.assignmentEditor)
        }
        if (this.$refs.assignmentEditor) this.$refs.assignmentEditor.scrollTop = 0
      })
    },
    closeAssignment () {
      if (this.assigning) return
      this.assignmentOpen = false
      this.selectedExistingTaxIds = []
      document.body.classList.remove('delivery-tax-editor-open')
    },
    assignExistingTaxes () {
      if (!this.deliveryRangeId || !this.selectedExistingTaxIds.length) return
      this.assigning = true
      this.apiRequest('/taxes-v2/delivery-ranges/' + this.deliveryRangeId + '/rules', 'POST', this.requestData({tax_rule_ids: this.selectedExistingTaxIds}), response => {
        this.assigning = false
        if (!response.status) return this.notify(this.responseMessage(response, 'Taxele nu au putut fi atribuite zonei.'), 'error')
        const assignedCount = ((response.data && response.data.attached_tax_rule_ids) || []).length
        this.notify(assignedCount === 1 ? 'Taxa a fost atribuită zonei.' : 'Taxele au fost atribuite zonei.')
        this.closeAssignment()
        this.loadRules()
        this.$emit('saved')
      })
    },
    openEdit (tax) {
      this.editorLoading = true
      this.openEditor()
      this.apiRequest('/taxes-v2/rules/' + tax.id, 'GET', this.requestData(), response => {
        this.editorLoading = false
        if (!response.status) {
          this.notify(this.responseMessage(response, 'Taxa nu a putut fi încărcată.'), 'error')
          this.closeEditor()
          return
        }
        this.form = Object.assign(emptyForm(), JSON.parse(JSON.stringify(response.data || {})))
        if (!Array.isArray(this.form.delivery_cost_ranges)) this.$set(this.form, 'delivery_cost_ranges', [])
      })
    },
    openEditor () {
      this.editorOpen = true
      document.body.classList.add('delivery-tax-editor-open')
      this.$nextTick(() => {
        if (this.$refs.editor && this.$refs.editor.parentNode !== document.body) {
          document.body.appendChild(this.$refs.editor)
        }
        if (this.$refs.editor) this.$refs.editor.scrollTop = 0
      })
    },
    closeEditor () {
      if (this.saving) return
      this.editorOpen = false
      this.editorLoading = false
      this.form = emptyForm()
      document.body.classList.remove('delivery-tax-editor-open')
    },
    save () {
      if (!this.form.name) return this.notify('Completează numele taxei.', 'error')
      if (this.form.vat_rate_id === '' || this.form.vat_rate_id === null) return this.notify('Selectează TVA-ul taxei.', 'error')
      if (Number(this.form.value) < 0) return this.notify('Costul taxei nu poate fi negativ.', 'error')
      const payload = Object.assign({}, JSON.parse(JSON.stringify(this.form)), {applies_to: 'delivery', calculation_type: 'fixed', method_ids: [3]})
      const isEdit = !!payload.id
      this.saving = true
      this.apiRequest('/taxes-v2/rules' + (isEdit ? '/' + payload.id : ''), isEdit ? 'PUT' : 'POST', this.requestData(payload), response => {
        this.saving = false
        if (!response.status) return this.notify(this.responseMessage(response, 'Taxa nu a putut fi salvată.'), 'error')
        this.notify(isEdit ? 'Taxa a fost actualizată.' : 'Taxa a fost creată.')
        this.closeEditor()
        this.loadRules()
        this.$emit('saved', response.data || payload)
      })
    },
    addCostRange () {
      this.form.delivery_cost_ranges.push({distance_in_km: '', shipping_cost: '', pos_id: '', min_order_value: 0, extra_delivery_time: 0})
    },
    removeCostRange (index) { this.form.delivery_cost_ranges.splice(index, 1) },
    existingTaxLabel (tax) { return `${tax.name} — ${this.money(tax.value)} ${this.currency}${tax.is_active ? '' : ' (inactivă)'}` },
    money (value) { return Number(value || 0).toLocaleString('ro-RO', {minimumFractionDigits: 2, maximumFractionDigits: 2}) },
    rate (value) { return Number(value || 0).toLocaleString('ro-RO', {maximumFractionDigits: 2}) },
    responseMessage (response, fallback) {
      const errorData = response && response.error && response.error.response && response.error.response.data
      if (response && response.message) return response.message
      if (errorData && errorData.message) return errorData.message
      if (errorData && errorData.errors) {
        const firstKey = Object.keys(errorData.errors)[0]
        const value = firstKey ? errorData.errors[firstKey] : null
        if (Array.isArray(value)) return value[0]
        if (value) return value
      }
      return fallback
    },
    notify (text, type) { swal({text, type: type || 'success', showConfirmButton: false, timer: 2200}) }
  }
}
</script>

<style lang="scss">body.delivery-tax-editor-open { overflow: hidden; }</style>
<style scoped lang="scss">
.delivery-tax-panel { background: #fff; border: 1px solid #dfe5f2; border-radius: 12px; box-shadow: 0 7px 22px rgba(29, 49, 95, .06); padding: 20px; width: 100%; }
.delivery-tax-panel__header, .delivery-tax-editor__section-title.with-action, .delivery-tax-range__header { align-items: center; display: flex; gap: 16px; justify-content: space-between; }
.delivery-tax-panel__actions { align-items: center; display: flex; flex-wrap: wrap; gap: 10px; justify-content: flex-end; }
.delivery-tax-panel__header h5, .delivery-tax-editor__section-title h4 { color: #173677; font-weight: 600; margin: 0 0 5px; }
.delivery-tax-panel__header p, .delivery-tax-editor__section-title p { color: #7d8daf; margin: 0; }
.delivery-tax-action { align-items: center; background: #22bea9; border: 0; border-radius: 24px; color: #fff; display: inline-flex; flex: 0 0 auto; font-size: 13px; font-weight: 600; gap: 8px; min-height: 42px; padding: 0 18px; }
.delivery-tax-action:hover { background: #16a994; }
.delivery-tax-action.is-secondary { background: #fff; border: 1px solid #22bea9; color: #129f8c; }
.delivery-tax-action.is-secondary:hover { background: #effbf8; }
.delivery-tax-empty { align-items: center; background: #f7f8fd; border-radius: 8px; color: #7d8daf; display: flex; gap: 9px; justify-content: center; margin-top: 18px; min-height: 64px; padding: 15px; text-align: center; }
.delivery-tax-list { display: grid; gap: 10px; margin-top: 18px; }
.delivery-tax-row { align-items: center; background: #f7f8fd; border: 1px solid #e3e8f4; border-radius: 9px; display: grid; gap: 18px; grid-template-columns: minmax(150px, 1.2fr) minmax(360px, 3fr) auto; padding: 14px 16px; }
.delivery-tax-row__title { display: flex; flex-direction: column; gap: 6px; }
.delivery-tax-row__title strong, .delivery-tax-row__details b { color: #173677; }
.delivery-tax-row__title span { border-radius: 12px; font-size: 11px; padding: 3px 8px; width: fit-content; }
.delivery-tax-row__title .is-active { background: #dff8f2; color: #059a7d; }
.delivery-tax-row__title .is-inactive { background: #eef0f5; color: #8490a8; }
.delivery-tax-row__details { display: grid; gap: 14px; grid-template-columns: repeat(4, minmax(70px, 1fr)); }
.delivery-tax-row__details span { display: flex; flex-direction: column; }
.delivery-tax-row__details small { color: #8796b6; margin-bottom: 3px; }
.delivery-tax-row__details b { font-weight: 500; }
.delivery-tax-edit { background: #fff; border: 1px solid #6a49da; border-radius: 18px; color: #6041ca; padding: 7px 12px; white-space: nowrap; }
.delivery-tax-editor { background: rgba(12, 25, 58, .55); bottom: 0; left: 0; overflow: auto; padding: 18px; position: fixed; right: 0; top: 0; z-index: 100000; }
.delivery-tax-editor__page { background: #f6f7fc; border-radius: 14px; box-shadow: 0 18px 60px rgba(13, 27, 62, .22); margin: 0 auto; max-width: 1240px; min-height: calc(100vh - 36px); overflow: hidden; width: 100%; }
.delivery-tax-assignment-page { max-width: 920px; }
.delivery-tax-editor__topbar { align-items: center; background: #fff; border-bottom: 1px solid #e3e7f1; display: flex; gap: 20px; justify-content: space-between; padding: 18px 24px; position: sticky; top: 0; z-index: 2; }
.delivery-tax-editor__heading { align-items: center; display: flex; gap: 20px; }
.delivery-tax-editor__topbar h3 { color: #173677; font-size: 20px; margin: 0 0 3px; }
.delivery-tax-editor__topbar p { color: #7d8daf; margin: 0; }
.delivery-tax-back { background: #fff; border: 1px solid #ff8b2c; border-radius: 22px; color: #f37a16; padding: 10px 18px; }
.delivery-tax-save { align-items: center; background: #dff7f3; border: 0; border-radius: 23px; color: #08a582; display: flex; font-weight: 600; gap: 8px; padding: 11px 20px; }
.delivery-tax-save img { height: 16px; width: 16px; }
.delivery-tax-editor__body { display: grid; gap: 18px; padding: 22px; }
.delivery-tax-editor__section { background: #fff; border: 1px solid #e1e6f2; border-radius: 11px; padding: 22px; }
.delivery-tax-editor__section-title { border-bottom: 1px solid #edf0f6; margin-bottom: 20px; padding-bottom: 15px; }
.delivery-tax-form-grid { display: grid; gap: 17px; grid-template-columns: repeat(3, minmax(0, 1fr)); }
.delivery-tax-form-grid .is-wide { grid-column: span 2; }
.delivery-tax-form-grid .form-group { margin: 0; }
.delivery-tax-form-grid label { color: #173677; display: block; font-weight: 500; margin-bottom: 7px; }
.delivery-tax-select { width: 100%; }
.delivery-tax-field-hint { color: #7d8daf; display: block; margin-top: 8px; }
.delivery-tax-switch { align-items: center; cursor: pointer; display: flex !important; gap: 9px; min-height: 42px; }
.delivery-tax-switch input { display: none; }
.delivery-tax-switch span { background: #cbd3e2; border-radius: 12px; height: 22px; position: relative; transition: .2s; width: 42px; }
.delivery-tax-switch span::after { background: #fff; border-radius: 50%; content: ''; height: 16px; left: 3px; position: absolute; top: 3px; transition: .2s; width: 16px; }
.delivery-tax-switch input:checked + span { background: #22bea9; }
.delivery-tax-switch input:checked + span::after { transform: translateX(20px); }
.delivery-tax-warning { align-items: center; background: #fff1f1; border: 1px solid #f2b6b6; border-radius: 8px; color: #bc3131; display: flex; gap: 12px; margin-bottom: 17px; padding: 13px 15px; }
.delivery-tax-range { background: #f8f9fd; border: 1px solid #e4e8f2; border-radius: 9px; margin-top: 12px; padding: 16px; }
.delivery-tax-range__header { color: #173677; margin-bottom: 14px; }
.delivery-tax-range__header button { background: transparent; border: 0; color: #ea6157; }
.delivery-tax-editor__loading { color: #7d8daf; padding: 80px 20px; text-align: center; }
@media (max-width: 950px) { .delivery-tax-row { grid-template-columns: 1fr; } .delivery-tax-row__details, .delivery-tax-form-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .delivery-tax-edit { justify-self: start; } }
@media (max-width: 620px) { .delivery-tax-panel__header, .delivery-tax-editor__section-title.with-action, .delivery-tax-editor__topbar, .delivery-tax-editor__heading { align-items: flex-start; flex-direction: column; } .delivery-tax-panel__actions, .delivery-tax-panel__actions .delivery-tax-action { justify-content: center; width: 100%; } .delivery-tax-row__details, .delivery-tax-form-grid { grid-template-columns: 1fr; } .delivery-tax-form-grid .is-wide { grid-column: auto; } .delivery-tax-editor { padding: 0; } .delivery-tax-editor__page { border-radius: 0; min-height: 100vh; } }
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Settings/Taxes/DeliveryTaxPanel.vue